/*******************************************************************************
* Licensed Materials - Property of HCL 
* (c) Copyright HCL Technologies Ltd. 2020. All Rights Reserved. 

* Note to U.S. Government Users Restricted Rights:
* Use, duplication or disclosure restricted by GSA ADP Schedule
**************************************************************/
/**
 * Component that creates a KPI Widget
 * required params:
 *  - position
 *  - label
 *  - value
 *  - status
 *
 */

import React, { useState, useEffect, Fragment } from "react";
import PropTypes from "prop-types";
import BackgroundPanel from "../BackgroundPanel";
import OverlayPanel from "../OverlayPanel";
const THREE = window.THREE;

function KPIWidgetPrototype({
    position,
    label,
    width,
    maxWidth,
    height,
    fontColor = "#c6c6c6",
    defaultColor = "#262626",
    hoverColor = "#363636",
    tile,
    value,
    settings,
    currentlyOpened,
    inVRMode,
    setKPI,
    setSettings,
    FrontPanelWidget,
    InfoWidget,
    SettingsWidget,
    aframeClasses,
    ...otherAttributes
}) {
    const [worldPosition, setWorldPosition] = useState({ x: 0, y: 0, z: 0 });
    const root = React.useRef();
    useEffect(() => {
        if (currentlyOpened && currentlyOpened[label]) {
            const parent = root.current.parentNode;
            const worldPos = new THREE.Vector3(0, 1.65, inVRMode ? -1.5 : -0.9);
            const localPos = parent.object3D.worldToLocal(worldPos);
            if (
                localPos.x !== worldPosition.x ||
                localPos.y !== worldPosition.y ||
                localPos.z !== worldPosition.z
            )
                setWorldPosition(localPos);
        }
    }, [currentlyOpened, label, worldPosition, inVRMode]);

    const isHoverAllowed =
        !currentlyOpened ||
        (inVRMode &&
            currentlyOpened[Object.keys(currentlyOpened)[0]] === "info");
    const pos = position.split(" ");

    const openPanelDimension = 1.15;

    // Event handler for closing settings
    // Checks if settings are changed
    const onCloseSettings = (updatedSettings) => {
        if (updatedSettings)
            setSettings((old) => {
                return { ...old, [label]: updatedSettings };
            });

        setKPI((old) => {
            return {
                ...old,
                opened: null
            };
        });
    };

    // Checks if KPI is in Alert
    const isAlert = () => {
        let res = false;
        if (settings && settings.actionEnabled) {
            const inputNumber = parseInt(settings.threshold);
            if (!isNaN(inputNumber)) {
                switch (settings.radio) {
                    case "Above":
                        res = value > inputNumber;
                        break;
                    case "Below":
                        res = value < inputNumber;
                        break;
                    default:
                        throw new Error("Invalid radio data");
                }
            }
        }
        return res;
    };

    const openCondition = Boolean(
        currentlyOpened &&
            currentlyOpened[label] &&
            (!inVRMode || currentlyOpened[label] === "settings")
    );

    return (
        <>
            <BackgroundPanel
                ref={root}
                onClick={() => {
                    if (isHoverAllowed && value) {
                        setKPI((old) => {
                            return {
                                ...old,
                                opened: { [label]: "info" }
                            };
                        });
                    }
                }}
                class={aframeClasses}
                position={position}
                width={width}
                height={height}
                backgroundColor={defaultColor}
                opacity={1}
                {...otherAttributes}
                animation__mouseenter_color={`property: color; startEvents:mouseenter; to: ${hoverColor}; dur:150; enabled:${
                    isHoverAllowed && !!value
                }`}
                animation__mouseleave_color={`property: color; startEvents:mouseleave; to: ${defaultColor}; dur:150; enabled:${isHoverAllowed}`}
                animation__mouseenter_position={`property: position; startEvents:mouseenter; to: ${
                    pos[0]
                } ${pos[1]} ${height / 5}; dur:150; enabled:${
                    isHoverAllowed && !!value
                }`}
                animation__mouseleave_position={`property: position; startEvents:mouseleave; to: ${pos[0]} ${pos[1]} ${pos[2]}; dur:150; enabled:${isHoverAllowed}`}
                animation__close_rotation={`property: rotation; autoplay: true; to: 0 0 0; dur:150; enabled: ${Boolean(
                    !currentlyOpened ||
                        (currentlyOpened && !currentlyOpened[label])
                )}`}
                animation__close_scale-width={`property: width; autoplay: true; to: ${width}; dur:150; enabled: ${Boolean(
                    !currentlyOpened ||
                        (currentlyOpened && !currentlyOpened[label])
                )}`}
                animation__close_scale-height={`property: height; autoplay: true; to: ${height}; dur:150; enabled: ${Boolean(
                    !currentlyOpened ||
                        (currentlyOpened && !currentlyOpened[label])
                )}`}
                animation__close_position={`property: position; autoplay: true;  to: ${pos[0]
                    } ${pos[1]} ${pos[2]}; dur:150; enabled: ${Boolean(
                        !currentlyOpened ||
                        (currentlyOpened && !currentlyOpened[label])
                    )}`}
                animation__open_rotation={`property: rotation; autoplay: true; to: 0 ${inVRMode ? -210 : -180
                    } 0; dur:150; enabled: ${openCondition}`}
                animation__open_position={`property: position; autoplay: true; to: ${worldPosition.x} ${worldPosition.y} ${worldPosition.z}; dur:150; enabled: ${openCondition}`}
                animation__open_scale-width={`property: width; autoplay: true; to: ${openPanelDimension}; dur:150; enabled: ${openCondition}`}
                animation__open_scale-height={`property: height; autoplay: true; to: ${openPanelDimension}; dur:150; enabled: ${openCondition}`}
                animation__open_color={`property: color; autoplay: true; to: ${defaultColor}; dur:150; enabled: ${openCondition}`}
                sound="src: #click; on: openKPI"
            >
                {(!currentlyOpened ||
                    (currentlyOpened && !currentlyOpened[label])) && (
                        <FrontPanelWidget
                            width={width}
                            maxWidth={maxWidth}
                            height={height}
                            isAlert={isAlert()}
                            label={label}
                            value={value}
                            tile={tile}
                            settingsOpenerHandler={() => {
                                setKPI((old) => {
                                    return {
                                        ...old,
                                        opened: { [label]: "settings" }
                                    };
                                });
                            }}
                            canOpenSettings={isHoverAllowed}
                            fontColor={fontColor}
                        />
                    )}
                {currentlyOpened &&
                    currentlyOpened[label] &&
                    currentlyOpened[label] === "settings" && (
                        <SettingsWidget
                            rotation="0 -180 0"
                            width={openPanelDimension}
                            height={openPanelDimension}
                            savedSettings={settings}
                            onClose={onCloseSettings}
                            label={label}
                        />
                    )}
                {currentlyOpened &&
                    currentlyOpened[label] &&
                    currentlyOpened[label] === "info" &&
                    !inVRMode && (
                        <InfoWidget
                            width={openPanelDimension}
                            height={openPanelDimension}
                            rotation="0 -180 0"
                            label={label}
                            onClose={() => {
                                setKPI((old) => {
                                    return {
                                        ...old,
                                        opened: null
                                    };
                                });
                            }}
                        />
                    )}
            </BackgroundPanel>
            {currentlyOpened && currentlyOpened[label] && !inVRMode && (
                <OverlayPanel
                    aframeClasses={aframeClasses}
                    position={`0 0 ${worldPosition.z * 0.8}`}
                    width={openPanelDimension * 10}
                    height={openPanelDimension * 10}
                    onClickHandler={() => {
                        setKPI((old) => {
                            return {
                                ...old,
                                opened: null
                            };
                        });
                    }}
                />
            )}
        </>
    );
}

// requiring data
KPIWidgetPrototype.propTypes = {
    label: PropTypes.string.isRequired,
    setKPI: PropTypes.func.isRequired,
    FrontPanelWidget: PropTypes.func.isRequired,
    InfoWidget: PropTypes.func.isRequired,
    SettingsWidget: PropTypes.func.isRequired
};

export default KPIWidgetPrototype;
